import React, { useEffect, useState } from 'react';
import { Routes } from '../../../../shared/routes';
import { AuthHelper, redirectWithoutRefresh } from '../../../../shared/utils';
import MCPAuthSuccess from './mcp-auth-success';

const MANUAL_LINK_DELAY_MS = 5000;
const PLAN_RESTRICTED_ERROR_MSG =
  "Your current plan doesn't support this integration. Upgrade your plan to continue.";

type AuthorizeResponse = {
  details?: unknown;
  error?: string;
  redirect_url?: string;
};

type PlanPermissionDetails = {
  code?: unknown;
  message?: unknown;
  type?: unknown;
};

type AuthorizeResponseRecord = Record<string, unknown>;

const getStringValue = (value: unknown) =>
  typeof value === 'string' ? value.trim() : '';

const parseMaybeJson = (value: unknown) => {
  let parsed = value;

  for (let index = 0; index < 2; index += 1) {
    if (typeof parsed !== 'string') {
      break;
    }

    try {
      parsed = JSON.parse(parsed);
    } catch {
      break;
    }
  }

  return parsed;
};

const getPlanRestrictionMessage = (data: AuthorizeResponse) => {
  const details = parseMaybeJson(data?.details);

  if (!details || typeof details !== 'object') {
    return '';
  }

  const parsedDetails = details as PlanPermissionDetails;
  const errorType = parsedDetails.type;
  const errorCode = Number(parsedDetails.code);

  if (errorType === 'plan-permission' || errorCode === 2001) {
    if (typeof parsedDetails.message === 'string') {
      return parsedDetails.message;
    }

    return PLAN_RESTRICTED_ERROR_MSG;
  }

  return '';
};

const parseAuthorizeResponse = (value: string): AuthorizeResponse => {
  const parsed = parseMaybeJson(value);

  if (!parsed || typeof parsed !== 'object') {
    return {};
  }

  return parsed as AuthorizeResponse;
};

const getRedirectUrl = (data: AuthorizeResponse, response: Response) => {
  const directRedirectUrl = getStringValue(data.redirect_url);
  if (directRedirectUrl) {
    return directRedirectUrl;
  }

  const record = data as AuthorizeResponseRecord;
  const camelRedirectUrl = getStringValue(record.redirectUrl);
  if (camelRedirectUrl) {
    return camelRedirectUrl;
  }

  const nestedData = record.data;
  if (nestedData && typeof nestedData === 'object') {
    const nestedRecord = nestedData as AuthorizeResponseRecord;
    const nestedSnakeCase = getStringValue(nestedRecord.redirect_url);
    if (nestedSnakeCase) {
      return nestedSnakeCase;
    }

    const nestedCamelCase = getStringValue(nestedRecord.redirectUrl);
    if (nestedCamelCase) {
      return nestedCamelCase;
    }
  }

  if (response.redirected) {
    const redirectedUrl = getStringValue(response.url);
    if (redirectedUrl) {
      return redirectedUrl;
    }
  }

  return '';
};

const MCPAuthSuccessContainer: React.FC = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const returnUrl = searchParams.get('returnUrl') || '';
  const [manualRedirectUrl, setManualRedirectUrl] = useState('');
  const [token] = useState(() => {
    const t = sessionStorage.getItem('mcp_token') || '';
    if (t) sessionStorage.removeItem('mcp_token');
    return t;
  });
  const shouldAttemptAuthorize = Boolean(returnUrl && token);
  const [status, setStatus] = useState<
    'authorizing' | 'success' | 'plan_restricted' | 'error'
  >(() => (shouldAttemptAuthorize ? 'authorizing' : 'success'));
  const [errorMsg, setErrorMsg] = useState('');
  const [showManualLink, setShowManualLink] = useState(false);

  useEffect(() => {
    if (!returnUrl) {
      setManualRedirectUrl('');
      return;
    }

    try {
      const authUrl = new URL(returnUrl);
      const redirectUri = authUrl.searchParams.get('redirect_uri') || '';
      setManualRedirectUrl(redirectUri);
    } catch {
      setManualRedirectUrl('');
    }
  }, [returnUrl]);

  // Automatic authorize call
  useEffect(() => {
    let isCancelled = false;
    let abortController: AbortController | null = null;

    if (!shouldAttemptAuthorize) {
      setStatus('success');
      return () => {
        isCancelled = true;
      };
    }

    setStatus('authorizing');
    setErrorMsg('');

    const authorize = async () => {
      try {
        const authUrl = new URL(returnUrl);
        const resource = authUrl.searchParams.get('resource') || '';
        const redirectUri = authUrl.searchParams.get('redirect_uri') || '';
        const mcpBase = resource.replace(/\/mcp\/?$/, '');

        if (!mcpBase) {
          if (!isCancelled) {
            setStatus('error');
            setErrorMsg('Could not determine MCP server URL');
          }
          return;
        }

        const proxyUrl = `${mcpBase}/authorize`;
        abortController = new AbortController();

        const response = await fetch(proxyUrl, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: authUrl.searchParams.toString(),
          signal: abortController.signal,
        });
        const rawBody = await response.text();
        const data = parseAuthorizeResponse(rawBody);

        if (isCancelled) {
          return;
        }

        const redirectUrl = getRedirectUrl(data, response);
        if (redirectUrl) {
          setStatus('success');
          window.location.replace(redirectUrl);
          return;
        }

        const restrictionError = getPlanRestrictionMessage(data);

        if (restrictionError) {
          setStatus('plan_restricted');
          setErrorMsg(restrictionError);
          return;
        }

        if (response.ok && redirectUri) {
          setStatus('success');
          window.location.replace(redirectUri);
          return;
        }

        setStatus('error');
        setErrorMsg(data.error || 'Authorization failed');
      } catch {
        if (!isCancelled) {
          setStatus('error');
          setErrorMsg('Failed to complete authorization. Please try again.');
        }
      }
    };

    authorize();

    return () => {
      isCancelled = true;
      abortController?.abort();
    };
  }, [returnUrl, shouldAttemptAuthorize, token]);

  // Manual redirect link timer (success state only)
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    if (status === 'success' && shouldAttemptAuthorize && manualRedirectUrl) {
      timer = setTimeout(() => {
        setShowManualLink(true);
      }, MANUAL_LINK_DELAY_MS);
    } else {
      setShowManualLink(false);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [manualRedirectUrl, shouldAttemptAuthorize, status]);

  const handleManualRedirect = () => {
    if (manualRedirectUrl) {
      window.location.href = manualRedirectUrl;
    }
  };

  const handlePricingRedirect = () => {
    if (token) {
      AuthHelper.login({ token, redirect: false });
    }
    redirectWithoutRefresh(Routes.Setting_Billing_Upgrade_Email_Outreach_Plan);
  };

  return (
    <MCPAuthSuccess
      status={status}
      errorMsg={errorMsg}
      returnUrl={returnUrl}
      showManualLink={showManualLink}
      onManualRedirect={handleManualRedirect}
      onPricingRedirect={handlePricingRedirect}
    />
  );
};

export default MCPAuthSuccessContainer;
